<template>
  <section>
    <THeading
      :level="3"
      tag="h3"
      class="mb-4"
    >
      {{ t('rules') }}
    </THeading>

    <p class="font-semibold">
      <span>{{ rvCheckInTimeFormatted }}</span>
      •
      <span>{{ rvCheckOutTimeFormatted }}</span>
    </p>

    <TGrid
      class="mt-4"
      has-vertical-gap
    >
      <TGridItem :lg="6">
        <div class="flex gap-3">
          <TIcon
            :icon="['fal', 'broom-wide']"
            size="xl"
            fixed-width
          />
          {{ t('rv.preferences.cleanliness') }}
        </div>
      </TGridItem>

      <!-- TODO: we check for !rvIsStatic in the template because when hosts
      change the RV type during the New Listing flow, we currently don't reset
      certain properties that don't exist on cottages. See:
      https://rvezy.atlassian.net/browse/RVZ-23766 -->
      <TGridItem
        v-if="rvAllowOnlyExperiencedSimilarRV && !rvIsStatic"
        :lg="6"
      >
        <div class="flex gap-3">
          <template v-if="rvIsMotorized">
            <TIcon
              :icon="['fal', 'car']"
              size="xl"
              fixed-width
            />
            {{ t('rv.preferences.experiencedDriverRequired') }}
          </template>
          <template v-else>
            <TIcon
              :icon="['fal', 'caravan-simple']"
              size="xl"
              fixed-width
            />
            {{ t('rv.preferences.towingExperienceRequired') }}
          </template>
        </div>
      </TGridItem>

      <TGridItem
        v-if="eventsParticipation && !rvIsStatic"
        :lg="6"
      >
        <div class="flex gap-3">
          <TIcon
            :icon="['fal', 'ban']"
            size="xl"
            fixed-width
          />
          {{ t('rv.preferences.noFestivalsOrEvents') }}
        </div>
      </TGridItem>

      <TGridItem
        v-if="!rvAllowCrossBorderTravel && !rvIsStatic"
        :lg="6"
      >
        <div class="flex gap-3">
          <TIcon
            :icon="['fal', 'ban']"
            size="xl"
            fixed-width
          />
          {{ t('rv.preferences.noCrossBorderTravel') }}
        </div>
      </TGridItem>

      <TGridItem
        v-if="!rvAllowPets"
        :lg="6"
      >
        <div class="flex gap-3">
          <TIcon
            icon="ban-paw"
            size="xl"
            fixed-width
          />
          {{ t('rv.preferences.noPetsAllowed') }}
        </div>
      </TGridItem>

      <TGridItem
        v-if="!rvAllowSmoking"
        :lg="6"
      >
        <div class="flex gap-3">
          <TIcon
            :icon="['fal', 'ban-smoking']"
            size="xl"
            fixed-width
          />
          {{ t('rv.preferences.noSmokingAllowed') }}
        </div>
      </TGridItem>
    </TGrid>
  </section>
</template>

<script setup lang="ts">
const {
  rvAllowCrossBorderTravel,
  rvAllowOnlyExperiencedSimilarRV,
  rvAllowFestivalEventsParticipation,
  rvAllowSmoking,
  rvAllowPets,
  rvCheckInStartTime,
  rvCheckInEndTime,
  rvCheckoutStartTime,
  rvCheckoutEndTime,
  rvIsMotorized,
  rvIsStatic,
} = useRvDetails()

const { t } = useI18n()

const eventsParticipation = computed(() => rvAllowFestivalEventsParticipation.value === 'No')

const { rvCheckInTimeFormatted, rvCheckOutTimeFormatted } = useRvCheckinCheckout({
  checkInStartTime: rvCheckInStartTime,
  checkInEndTime: rvCheckInEndTime,
  checkOutStartTime: rvCheckoutStartTime,
  checkOutEndTime: rvCheckoutEndTime,
  isStatic: rvIsStatic,
})
</script>

<i18n lang="json">
{
  "en": {
    "rules": "RV rules"
  },
  "fr": {
    "rules": "Règles relatives au VR"
  }
}
</i18n>
